/**
 * The dashboard's HCL renderer (Feature: terraform-provider, task 10.1; Requirements 14.1–14.5;
 * Design: §Components 11, Algorithm L).
 *
 * Turns a `CheckDto` or an `AlertRuleDto` the page has **already loaded** into the `terraform import`
 * command and the `resource` stanza that adopts it, so bringing a dashboard-created monitor under
 * Terraform needs no id hunting and no hand-written HCL (Requirement 14.1, 14.2). Every function here
 * is **pure** — no client, no clock, no `Date`, no randomness — which is what lets Requirement 14.7 hold
 * (nothing to fetch) and what makes Properties 9 and 10 testable without a server.
 *
 * Three things carry the whole feature, and each is easy to get subtly wrong:
 *
 * ## 1. `${` must become `$${`, or every stanza is quietly broken
 *
 * The platform's runtime placeholders — `${secret:<ref>}` for a secret reference and `${<name>}` for a
 * value an earlier API step extracted (`substitute`, `services/src/execution/api-runner.ts`) — share
 * HCL's interpolation syntax exactly. A stanza that emits `"Bearer ${secret:shop/token}"` does not carry
 * that reference to the platform: Terraform evaluates `${secret:shop/token}` as an HCL expression and
 * fails, or worse resolves it to something else. Written `$${…}`, Terraform hands the provider the
 * literal `${…}`, which is what the API stores and the runner resolves. So the escape is not cosmetic
 * quoting — it is the difference between a stanza that works and one that looks right and is not.
 *
 * ## 2. A masked value must be honest, not plausible
 *
 * The API redacts any field whose *name* matches a credential pattern, replacing the value with
 * `***REDACTED***`. Rendering that verbatim would produce a stanza that applies cleanly and silently
 * repoints a header at the literal text `***REDACTED***`. So a masked position renders as
 * `"REPLACE_ME"` plus a comment saying where the real value has to come from (Requirement 14.3) — a
 * stanza that refuses to pretend is worth more than one that plans clean.
 *
 * `***REDACTED***` is compared as a **literal string** here. It exists as a constant in `@soroush/core`,
 * and importing it would break `vite build`: core is emitted as CommonJS, so Rollup cannot resolve a
 * named export through its `__exportStar(require(...))` re-export. It type-checks, it passes vitest, and
 * it fails only at bundle time — the same trap `CHECK_PAYLOAD_DEFAULTS` documents in `api/types.ts`. The
 * copy is kept honest by a test, which is never bundled and so may import the real thing freely.
 *
 * ## 3. Server defaults are omitted, so the stanza plans clean after import
 *
 * `enabled = true`, `failure_threshold = 1`, `max_retries = 2`, `latency_sensitive = false`, and a
 * `resource_profile` equal to the per-type default are all things the *server* supplies. The provider's
 * schema gives each one a framework `Default` (or `UseStateForUnknown`) equal to the platform's, so
 * writing them out is redundant — and writing a value the provider would compute differently is worse
 * than redundant. The two sets of numbers therefore have to be the *same* numbers, which is why they are
 * read from `platform-constants.json` — the generated golden file task 5.1 exports for exactly this
 * cross-repository purpose — rather than copied by hand into a literal that can drift silently.
 */

import type { AlertRuleDto, CheckDto } from '../api/types';

// The golden constants file, imported as JSON. This is NOT a runtime import from `@soroush/core`: JSON
// is inlined by the bundler as plain data, so it carries none of core's CommonJS interop problem. The
// file is generated and verified by `core/src/domain/platform-constants.golden.test.ts`, and the
// Provider_Repository checks in its own copy of it, so the dashboard's omission rules and the provider's
// framework `Default`s cannot drift apart without a test somewhere going red.
import platformConstants from '../../../core/src/domain/__golden__/platform-constants.json';

/* -------------------------------------------------------------------------- */
/* Platform constants                                                          */
/* -------------------------------------------------------------------------- */

/** `CHECK_DEFAULTS` — the values the server supplies when a create omits them. */
const CHECK_DEFAULTS = platformConstants.CHECK_DEFAULTS;

/** `DEFAULT_RESOURCE_PROFILES` — the per-Check-type profile the server computes. */
const DEFAULT_RESOURCE_PROFILES: Readonly<
  Record<string, { compute: string; memoryMb: number; timeoutMs: number }>
> = platformConstants.DEFAULT_RESOURCE_PROFILES;

/**
 * The Masked_Value the read API substitutes for a credential-shaped field.
 *
 * A literal, deliberately — see the module doc. `hcl.constants.test.ts` asserts it equals core's
 * `SECRET_MASK`, so the copy cannot drift without failing.
 */
export const MASKED_VALUE = '***REDACTED***';

/** What a masked position renders as instead (Requirement 14.3). */
const MASK_PLACEHOLDER = '"REPLACE_ME"';

/** The comment that accompanies every {@link MASK_PLACEHOLDER} (Requirement 14.3). */
const MASK_COMMENT = '# value masked by the API; supply it from the original configuration';

/**
 * The reference recommendation on a `logical_names` selector (Requirement 14.4).
 *
 * `<name>` stays literal: the dashboard renders one entity at a time and does not know what Terraform
 * resource name the reader will give the Checks this rule selects, so naming a specific one would be a
 * guess. The sentence is a recommendation, and a literal selector still works — what it loses is graph
 * ordering, which is why the recommendation is here at all (Design: §Components 15).
 */
const SELECTOR_REFERENCE_COMMENT = '# prefer soroush_check.<name>.logical_name references';

/* -------------------------------------------------------------------------- */
/* Identifiers (Requirement 14.4; Property 10)                                 */
/* -------------------------------------------------------------------------- */

/** A valid Terraform resource name: what {@link toHclIdentifier} always returns. */
const HCL_IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_-]*$/;

/** The name given to a string that normalises to nothing at all. */
const FALLBACK_IDENTIFIER = 'resource';

/**
 * Normalise any string into a valid Terraform resource name (Requirement 14.4, Algorithm L).
 *
 * Total and idempotent (Property 10): every character outside `[A-Za-z0-9_-]` becomes `_`, runs of the
 * replacement collapse to one, a leading digit or `-` gains a `_` prefix, and a string that yields
 * nothing becomes `resource`. The result always matches {@link HCL_IDENTIFIER}, and applying it twice
 * equals applying it once — which is what lets {@link renderImportCommand} and
 * {@link renderCheckResource} each normalise independently and still agree on the name.
 *
 * Idempotence is the constraint that shapes the implementation. Collapsing runs of `_` *only where the
 * substitution created them* would not be idempotent, because `a__b` (a legitimate input) would collapse
 * on the first pass and not the second. So runs collapse unconditionally: `a__b` and `a!!b` both become
 * `a_b`, and both are then fixed points. Losing the distinction between them is the price of a function
 * that can be applied twice safely, and no caller depends on that distinction.
 *
 * There is deliberately **no** final `HCL_IDENTIFIER.test(...)` safety net before the return. Every
 * branch below establishes the shape by construction — the substitution leaves only `[A-Za-z0-9_-]`, and
 * the prefix step is exactly the case where the first character is not a legal start — so a net could
 * only ever fire if this function were later edited into a bug, and its effect would be to turn that bug
 * into a silent `"resource"`. That is the one outcome Property 10 could not see. The property is the net.
 */
export function toHclIdentifier(name: string): string {
  const substituted = name.replace(/[^A-Za-z0-9_-]/g, '_');
  // Collapse runs of `_` unconditionally — see the doc comment on why this cannot be conditional.
  const collapsed = substituted.replace(/_{2,}/g, '_');
  // `''` from an empty or wholly-substituted input; `'_'` from a run of separators, which carries no
  // more information than `''` does and would otherwise become a stanza named `_`.
  if (collapsed === '' || collapsed === '_') return FALLBACK_IDENTIFIER;
  // A digit or `-` is legal *within* an identifier and not at its start.
  return /^[0-9-]/.test(collapsed) ? `_${collapsed}` : collapsed;
}

/* -------------------------------------------------------------------------- */
/* Escaping (Requirement 10.1; the point of the feature — see the module doc)   */
/* -------------------------------------------------------------------------- */

/**
 * Escape a string for an HCL quoted template.
 *
 * Beyond the design's list (`\"`, `\\`, `\n`, `\t`, `${`) this also escapes `\r` and `%{`, for the same
 * reason the list exists. `%{` opens an HCL *template directive* — `%{if …}` — so an unescaped one is a
 * syntax error exactly as an unescaped `${` is, and a bare carriage return terminates the line. Neither
 * appears in a platform placeholder, so neither is likely; both are reachable from a customer's own URL,
 * header value, request body, or browser script, and Requirement 14.3 asks for a stanza that plans
 * clean, which an unparseable one cannot do. Recorded here as an addition rather than left implicit.
 */
const HCL_ESCAPES: ReadonlyMap<string, string> = new Map([
  ['\\', '\\\\'],
  ['"', '\\"'],
  ['\n', '\\n'],
  ['\r', '\\r'],
  ['\t', '\\t'],
  ['${', '$${'],
  ['%{', '%%{'],
]);

function escapeHclString(value: string): string {
  // ONE pass over an alternation, and a **function** replacer, both deliberately.
  //
  // The function replacer is not a style choice: in a replacement *string*, `$$` is the escape for a
  // literal `$`, so the obvious `.replace(/\$\{/g, '$${')` emits `${` — it is a no-op that looks exactly
  // like the fix. It type-checks, it reads correctly, and it silently ships unescaped placeholders. (It
  // was written that way here first; the property test's `${` invariant is what caught it.) A replacer
  // function receives no `$` interpretation at all, so the hazard cannot recur.
  //
  // One pass rather than a chain of `.replace()` calls removes the other trap: a chain is only correct
  // for one ordering of the rules, because a later pass can escape a `\` an earlier pass just inserted.
  // Matching each token once against the original string has no ordering to get wrong.
  return value.replace(/[\\"\n\r\t]|\$\{|%\{/g, (token) => HCL_ESCAPES.get(token) ?? token);
}

/** A quoted HCL string literal. */
function quote(value: string): string {
  return `"${escapeHclString(value)}"`;
}

/* -------------------------------------------------------------------------- */
/* The document model                                                          */
/* -------------------------------------------------------------------------- */

/**
 * One line (or one nested group of lines) of a stanza.
 *
 * A small model rather than string concatenation, because two things have to be decided *after* a
 * block's contents are known: the `=` alignment across a run of scalar attributes, and the indentation
 * of everything nested inside. Building the tree first and rendering once keeps both in one place.
 */
type HclNode =
  /** `key = value`, optionally with a trailing comment. */
  | { readonly kind: 'attr'; readonly key: string; readonly value: string; readonly comment?: string }
  /** `key = { … }` over several lines. */
  | { readonly kind: 'block'; readonly key: string; readonly children: readonly HclNode[] }
  /** `key = [{ … }, { … }]` over several lines. */
  | { readonly kind: 'list'; readonly key: string; readonly items: readonly (readonly HclNode[])[] }
  /** A standalone comment line. */
  | { readonly kind: 'comment'; readonly text: string };

function attr(key: string, value: string, comment?: string): HclNode {
  return comment === undefined ? { kind: 'attr', key, value } : { kind: 'attr', key, value, comment };
}

const INDENT = '  ';

/**
 * Render a node list at one indentation level.
 *
 * The `=` signs of a **contiguous run** of single-line attributes are aligned, and anything that is not
 * one (a nested block, a list, a comment) ends the run. That is `terraform fmt`'s own rule, so a reader
 * who runs `fmt` over a pasted stanza should see no diff — though note that no `terraform` binary exists
 * in this repository's toolchain, so that claim is reasoned from fmt's documented behaviour and pinned
 * by the golden fixtures rather than verified against fmt itself.
 */
function renderNodes(nodes: readonly HclNode[], depth: number): string[] {
  const pad = INDENT.repeat(depth);
  const out: string[] = [];
  let index = 0;

  while (index < nodes.length) {
    const node = nodes[index]!;

    if (node.kind === 'attr') {
      // Gather the run, then align to its widest key.
      let end = index;
      while (end < nodes.length && nodes[end]!.kind === 'attr') end += 1;
      const run = nodes.slice(index, end) as Extract<HclNode, { kind: 'attr' }>[];
      const width = Math.max(...run.map((a) => a.key.length));
      for (const a of run) {
        const line = `${pad}${a.key.padEnd(width)} = ${a.value}`;
        out.push(a.comment === undefined ? line : `${line} ${a.comment}`);
      }
      index = end;
      continue;
    }

    if (node.kind === 'block') {
      out.push(`${pad}${node.key} = {`);
      out.push(...renderNodes(node.children, depth + 1));
      out.push(`${pad}}`);
      index += 1;
      continue;
    }

    if (node.kind === 'list') {
      out.push(`${pad}${node.key} = [`);
      for (const item of node.items) {
        out.push(`${pad}${INDENT}{`);
        out.push(...renderNodes(item, depth + 2));
        // A trailing comma on every element, including the last: valid in a multi-line HCL tuple, and
        // what `fmt` leaves in place.
        out.push(`${pad}${INDENT}},`);
      }
      out.push(`${pad}]`);
      index += 1;
      continue;
    }

    out.push(`${pad}${node.text}`);
    index += 1;
  }

  return out;
}

/** Wrap a node list in `resource "<type>" "<name>" { … }`, with any leading comment lines above it. */
function renderResource(
  resourceType: string,
  name: string,
  children: readonly HclNode[],
  leadingComments: readonly string[],
): string {
  const lines = [
    ...leadingComments,
    `resource "${resourceType}" "${toHclIdentifier(name)}" {`,
    ...renderNodes(children, 1),
    '}',
  ];
  return `${lines.join('\n')}\n`;
}

/* -------------------------------------------------------------------------- */
/* Value helpers                                                               */
/* -------------------------------------------------------------------------- */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Read an object-shaped value defensively; anything else reads as empty. */
function asRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** The strings of an array-shaped value, in source order; a non-array reads as empty. */
function stringsOf(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

/** A number literal. HCL has no `NaN`/`Infinity`, which {@link isFiniteNumber} already excludes. */
function numberLiteral(value: number): string {
  return String(value);
}

/**
 * A set attribute, emitted **sorted** so the rendering is deterministic and order-free on the wire
 * (Design: §Data Models, "set attributes … emitted sorted").
 *
 * A masked element becomes `"REPLACE_ME"` in place, and the line carries {@link MASK_COMMENT} once — a
 * single line can hold only one trailing comment, and the placeholders themselves mark every position.
 */
function setLiteral(values: readonly string[]): { value: string; comment?: string } {
  const sorted = [...values].sort();
  const masked = sorted.some((v) => v === MASKED_VALUE);
  const rendered = sorted.map((v) => stringValue(v).value);
  return masked
    ? { value: `[${rendered.join(', ')}]`, comment: MASK_COMMENT }
    : { value: `[${rendered.join(', ')}]` };
}

/**
 * A string value as HCL: quoted, or the placeholder plus its comment when it is a Masked_Value.
 *
 * **Every** string that reaches the output goes through here. Not because every field can realistically
 * be masked — redaction is driven by a field's *name*, so `project_id` and `resource_profile.compute`
 * never will be — but because "at every masked position and nowhere else" (Property 9) is only checkable
 * if there is exactly one place that decides it. A second path that quoted a string directly would emit
 * the literal text `***REDACTED***` into a stanza on the day the platform's redaction rules widen, and
 * nothing here would notice.
 */
function stringValue(value: string): { value: string; comment?: string } {
  return value === MASKED_VALUE
    ? { value: MASK_PLACEHOLDER, comment: MASK_COMMENT }
    : { value: quote(value) };
}

/** Push `key = "<string>"`, honouring a Masked_Value, when the value is a string at all. */
function pushString(nodes: HclNode[], key: string, value: unknown): void {
  if (typeof value !== 'string') return;
  const { value: rendered, comment } = stringValue(value);
  nodes.push(attr(key, rendered, comment));
}

/** Push `key = <number>` when the value is a finite number. */
function pushNumber(nodes: HclNode[], key: string, value: unknown): void {
  if (isFiniteNumber(value)) nodes.push(attr(key, numberLiteral(value)));
}

/** Push `key = true|false` when the value is a boolean. */
function pushBoolean(nodes: HclNode[], key: string, value: unknown): void {
  if (typeof value === 'boolean') nodes.push(attr(key, String(value)));
}

/** Push a sorted set attribute when it has at least one member (`[]` reads as null — Design §Data Models). */
function pushSet(nodes: HclNode[], key: string, values: readonly string[]): void {
  if (values.length === 0) return;
  const { value, comment } = setLiteral(values);
  nodes.push(attr(key, value, comment));
}

/**
 * A map key as HCL: **always quoted**.
 *
 * A bare identifier key is legal in an HCL object constructor, and HCL identifiers do admit `-`, so
 * `Content-Type = "application/json"` reads nicely and — as far as the grammar goes — parses. That was
 * the first version here, emitting bare keys whenever they matched {@link HCL_IDENTIFIER}.
 *
 * It is not worth the risk. Nothing in this repository's toolchain can run `terraform`, so "a bare
 * hyphenated key lexes as one identifier and is then taken as a literal string" is a claim about someone
 * else's lexer that no test here can check, and the failure mode if it is wrong is a stanza that does not
 * parse — for the *most* ordinary header name there is. A quoted key needs no such claim: it is a string
 * literal in a position where a string literal is unambiguously allowed, it goes through
 * {@link escapeHclString} like every other string, and it stays correct for a header name with a dot, a
 * space, or a `${` in it. The cost is cosmetic and the guarantee is total, which is the right trade for a
 * generator whose whole purpose is output someone else pastes.
 */
function mapKey(key: string): string {
  return quote(key);
}

/** A `map(string)` attribute as a nested block, sorted by key, each value honouring a Masked_Value. */
function pushStringMap(nodes: HclNode[], key: string, value: unknown): void {
  const record = asRecord(value);
  const entries = Object.entries(record)
    .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  if (entries.length === 0) return;
  const children: HclNode[] = [];
  for (const [name, raw] of entries) {
    const { value: rendered, comment } = stringValue(raw);
    // `mapKey(name)` and not `stringValue(name)`: redaction replaces a *value*, so a header whose NAME
    // is the Masked_Value is a header genuinely called that, and renaming it would be the lie.
    children.push(attr(mapKey(name), rendered, comment));
  }
  nodes.push({ kind: 'block', key, children });
}

/**
 * A single-line object literal, for fixed shapes of two or three numeric fields.
 *
 * Restricted to shapes that cannot contain a string, deliberately: a masked value needs a trailing
 * comment, and a comment cannot be attached to one field of a single-line object without commenting out
 * the rest of the line.
 */
function inlineObject(fields: readonly (readonly [string, string])[]): string {
  return `{ ${fields.map(([k, v]) => `${k} = ${v}`).join(', ')} }`;
}

/* -------------------------------------------------------------------------- */
/* Check specs                                                                 */
/* -------------------------------------------------------------------------- */

function uptimeNodes(spec: unknown): HclNode[] {
  const s = asRecord(spec);
  const nodes: HclNode[] = [];
  pushString(nodes, 'mode', s.mode);
  pushString(nodes, 'target', s.target);
  pushNumber(nodes, 'port', s.port);
  pushNumber(nodes, 'connect_timeout_ms', s.connectTimeoutMs);
  pushNumber(nodes, 'heartbeat_interval_seconds', s.heartbeatIntervalSeconds);
  const range = asRecord(s.expectedStatusRange);
  if (isFiniteNumber(range.min) && isFiniteNumber(range.max)) {
    nodes.push(
      attr(
        'expected_status_range',
        inlineObject([
          ['min', numberLiteral(range.min)],
          ['max', numberLiteral(range.max)],
        ]),
      ),
    );
  }
  return nodes;
}

function apiStepNodes(step: unknown): HclNode[] {
  const s = asRecord(step);
  const nodes: HclNode[] = [];
  pushString(nodes, 'method', s.method);
  pushString(nodes, 'url', s.url);
  pushNumber(nodes, 'expected_status', s.expectedStatus);
  pushString(nodes, 'body', s.body);
  pushStringMap(nodes, 'headers', s.headers);

  const assertions = Array.isArray(s.assertions) ? s.assertions : [];
  if (assertions.length > 0) {
    nodes.push({
      kind: 'list',
      key: 'assertion',
      items: assertions.map((raw) => {
        const a = asRecord(raw);
        const item: HclNode[] = [];
        pushString(item, 'source', a.source);
        pushString(item, 'comparator', a.comparator);
        pushString(item, 'path', a.path);
        pushString(item, 'expected', a.expected);
        return item;
      }),
    });
  }

  const extracts = Array.isArray(s.extracts) ? s.extracts : [];
  if (extracts.length > 0) {
    nodes.push({
      kind: 'list',
      key: 'extract',
      items: extracts.map((raw) => {
        const e = asRecord(raw);
        const item: HclNode[] = [];
        pushString(item, 'name', e.name);
        pushString(item, 'source', e.source);
        pushString(item, 'path', e.path);
        return item;
      }),
    });
  }

  return nodes;
}

function apiNodes(spec: unknown): HclNode[] {
  const s = asRecord(spec);
  const nodes: HclNode[] = [];
  pushNumber(nodes, 'response_time_threshold_ms', s.responseTimeThresholdMs);
  const steps = Array.isArray(s.steps) ? s.steps : [];
  if (steps.length > 0) {
    nodes.push({ kind: 'list', key: 'step', items: steps.map(apiStepNodes) });
  }
  return nodes;
}

function browserNodes(spec: unknown): HclNode[] {
  const s = asRecord(spec);
  const nodes: HclNode[] = [];
  pushString(nodes, 'script', s.script);
  pushNumber(nodes, 'max_execution_ms', s.maxExecutionMs);
  pushBoolean(nodes, 'otel_enabled', s.otelEnabled);
  return nodes;
}

/** The one spec attribute a Check's `type` selects (`uptime` / `api` / `browser`). */
const SPEC_RENDERERS: Readonly<Record<string, (spec: unknown) => HclNode[]>> = {
  uptime: uptimeNodes,
  api: apiNodes,
  browser: browserNodes,
};

/* -------------------------------------------------------------------------- */
/* Resource-profile omission                                                   */
/* -------------------------------------------------------------------------- */

/**
 * `resource_profile`, but only when it differs from the server's per-type default.
 *
 * The provider declares this attribute Optional+Computed with `UseStateForUnknown`, so an omitted
 * profile is read back from the API and kept — writing out a profile that equals the default would add a
 * line that says nothing. All three fields are emitted when any one differs, because the API stores a
 * complete profile and a partial object would leave the rest unknown.
 */
function pushResourceProfile(nodes: HclNode[], type: string, value: unknown): void {
  if (!isRecord(value)) return;
  const compute = value.compute;
  const memoryMb = value.memoryMb;
  const timeoutMs = value.timeoutMs;
  if (typeof compute !== 'string' || !isFiniteNumber(memoryMb) || !isFiniteNumber(timeoutMs)) return;

  // `Object.hasOwn`, not a bare index: `type` reaches here from wire data, and an object-literal lookup
  // inherits `Object.prototype` — the hazard `api/writes.ts` avoids with a `Map` and `Badges.tsx`'s
  // `OriginBadge` avoids the same way. A type of `constructor` must find no default, not a function.
  const fallback = Object.hasOwn(DEFAULT_RESOURCE_PROFILES, type)
    ? DEFAULT_RESOURCE_PROFILES[type]
    : undefined;
  if (
    fallback !== undefined &&
    fallback.compute === compute &&
    fallback.memoryMb === memoryMb &&
    fallback.timeoutMs === timeoutMs
  ) {
    return;
  }

  const children: HclNode[] = [];
  pushString(children, 'compute', compute);
  children.push(attr('memory_mb', numberLiteral(memoryMb)));
  children.push(attr('timeout_ms', numberLiteral(timeoutMs)));
  nodes.push({ kind: 'block', key: 'resource_profile', children });
}

/* -------------------------------------------------------------------------- */
/* Manifest provenance (Requirement 14.5; Security finding 6)                   */
/* -------------------------------------------------------------------------- */

/**
 * The leading comment a Manifest-Origin entity gets (Requirement 14.5, 7.4; Security finding 6).
 *
 * It is placed above the stanza rather than inside it because it is a statement about what *applying*
 * this file does, not about any one attribute — and because a reader deciding whether to adopt the
 * entity at all needs it before they read the configuration, not after. The consequence is one-way and
 * silent otherwise: re-claiming a manifest entity to `terraform` removes it from `PUT /v1/deployments`
 * reconciliation, so the code-first team's next deploy reports it as a **conflict** rather than a change.
 */
function manifestComments(entity: 'check' | 'alert rule'): string[] {
  return [
    `# This ${entity} is currently managed by the deployment manifest.`,
    '# Importing it and applying this stanza transfers it to Terraform for good: it leaves manifest',
    "# reconciliation permanently, and the next `PUT /v1/deployments` reports it as a conflict rather",
    '# than a change. Re-declare it in code or delete it there first if that is not what you want.',
  ];
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                  */
/* -------------------------------------------------------------------------- */

/** The two resource types the provider declares. */
export type SoroushResourceType = 'soroush_check' | 'soroush_alert_rule';

/**
 * The exact `terraform import` command for an entity (Requirement 14.1, 14.2).
 *
 * The name is normalised here as well as in the renderers. {@link toHclIdentifier} is idempotent, so a
 * caller that already normalised loses nothing, and a caller that did not cannot produce a command whose
 * address disagrees with the stanza it accompanies.
 */
export function renderImportCommand(kind: SoroushResourceType, name: string, id: string): string {
  return `terraform import ${kind}.${toHclIdentifier(name)} ${id}`;
}

/**
 * The `resource "soroush_check"` stanza that adopts a Check (Requirements 14.1, 14.3, 14.5).
 *
 * Rendered from the DTO the page already holds; `state` and every other runtime field is simply not read
 * (Requirement 8.4's rule, applied at the other end).
 */
export function renderCheckResource(check: CheckDto, name: string): string {
  const nodes: HclNode[] = [];

  pushString(nodes, 'project_id', check.projectId);
  pushString(nodes, 'logical_name', check.logicalName);
  pushNumber(nodes, 'interval_seconds', check.intervalSeconds);
  pushSet(nodes, 'locations', stringsOf(check.locations));
  pushSet(nodes, 'secrets_refs', stringsOf(check.secretsRefs));

  // Server defaults, omitted when equal — see the module doc's third point.
  if (check.enabled !== CHECK_DEFAULTS.enabled) pushBoolean(nodes, 'enabled', check.enabled);
  if (check.failureThreshold !== CHECK_DEFAULTS.failureThreshold) {
    pushNumber(nodes, 'failure_threshold', check.failureThreshold);
  }
  if (check.maxRetries !== CHECK_DEFAULTS.maxRetries) {
    pushNumber(nodes, 'max_retries', check.maxRetries);
  }
  if (check.latencySensitive !== CHECK_DEFAULTS.latencySensitive) {
    pushBoolean(nodes, 'latency_sensitive', check.latencySensitive);
  }

  const warmPool = asRecord(check.warmPool);
  if (isFiniteNumber(warmPool.min)) {
    nodes.push(attr('warm_pool', inlineObject([['min', numberLiteral(warmPool.min)]])));
  }

  pushResourceProfile(nodes, check.type, check.resourceProfile);

  // Exactly one of uptime / api / browser (Property 9). Own-property lookup for the same reason
  // `pushResourceProfile` uses one.
  if (Object.hasOwn(SPEC_RENDERERS, check.type)) {
    nodes.push({ kind: 'block', key: check.type, children: SPEC_RENDERERS[check.type]!(check.spec) });
  } else {
    // Unreachable through `CheckType`, and reachable from a platform that grew a fourth Check type
    // before this bundle was refreshed. Saying so is better than emitting a stanza that silently has no
    // spec at all.
    nodes.push({
      kind: 'comment',
      text: `# unrecognised check type ${JSON.stringify(check.type)}: this dashboard build cannot render its spec`,
    });
  }

  return renderResource(
    'soroush_check',
    name,
    nodes,
    check.origin === 'manifest' ? manifestComments('check') : [],
  );
}

/** The `check_selector` block, with the reference recommendation on a `logical_names` selector (14.4). */
function checkSelectorNode(rule: AlertRuleDto): HclNode | undefined {
  const selector = rule.checkSelector;
  if (!isRecord(selector)) return undefined;

  if (selector.kind === 'all') {
    return { kind: 'block', key: 'check_selector', children: [attr('all', 'true')] };
  }

  if (selector.kind === 'logicalName') {
    const names = stringsOf(selector.names);
    if (names.length === 0) return undefined;
    const { value } = setLiteral(names);
    // Requirement 14.4: literals, plus the comment recommending references. A masked name is not
    // possible — `names` is not credential-shaped — so the set's own mask comment is not in play here,
    // and this comment takes the line.
    return {
      kind: 'block',
      key: 'check_selector',
      children: [attr('logical_names', value, SELECTOR_REFERENCE_COMMENT)],
    };
  }

  if (selector.kind === 'checkId') {
    const ids = stringsOf(selector.checkIds);
    if (ids.length === 0) return undefined;
    const { value } = setLiteral(ids);
    return { kind: 'block', key: 'check_selector', children: [attr('check_ids', value)] };
  }

  return undefined;
}

/**
 * The `resource "soroush_alert_rule"` stanza that adopts an Alert_Rule (Requirements 14.2, 14.4, 14.5).
 */
export function renderAlertRuleResource(rule: AlertRuleDto, name: string): string {
  const nodes: HclNode[] = [];

  pushString(nodes, 'project_id', rule.projectId);

  const selector = checkSelectorNode(rule);
  if (selector !== undefined) nodes.push(selector);

  pushString(nodes, 'fire_on', rule.fireOn);
  pushString(nodes, 'severity', rule.severity);

  const rate = asRecord(rule.maxNotificationRate);
  if (isFiniteNumber(rate.maxNotifications) && isFiniteNumber(rate.windowSeconds)) {
    nodes.push(
      attr(
        'max_notification_rate',
        inlineObject([
          ['max_notifications', numberLiteral(rate.maxNotifications)],
          ['window_seconds', numberLiteral(rate.windowSeconds)],
        ]),
      ),
    );
  }

  pushSet(nodes, 'channels', stringsOf(rule.channels));
  pushSet(nodes, 'integrations', stringsOf(rule.integrations));
  pushNumber(nodes, 'consecutive_failures', rule.consecutiveFailures);
  pushString(nodes, 'grouping_key', rule.groupingKey);

  return renderResource(
    'soroush_alert_rule',
    name,
    nodes,
    rule.origin === 'manifest' ? manifestComments('alert rule') : [],
  );
}

/**
 * The `required_providers` + `provider "soroush"` snippet (Requirement 14.6).
 *
 * `endpoint` is a literal because it is the deployment's own API endpoint and not a secret. `api_key` is
 * deliberately **absent**: the provider falls back to `SOROUSH_API_KEY`, and a literal here would be
 * recorded verbatim in any saved plan file (`terraform plan -out`), which is the one place the platform's
 * only plaintext credential could otherwise come to rest (Security finding 1). The comment says so, in
 * the snippet, because the snippet is what gets pasted — a warning that lives only in the dashboard is a
 * warning that does not travel.
 *
 * No `version` constraint. Pinning one is good practice and would name a release that does not exist
 * yet — the provider ships in Milestone 2 — so `terraform init` would fail on a version constraint
 * before it failed on a missing provider. The recommendation to pin is stated instead.
 */
export function renderProviderSetup(endpoint: string, source = 'DanaAIhq/soroush'): string {
  return [
    'terraform {',
    '  required_providers {',
    '    soroush = {',
    `      source = ${quote(source)}`,
    '      # Pin a version once the provider is published, e.g. version = "~> 0.1".',
    '    }',
    '  }',
    '}',
    '',
    'provider "soroush" {',
    `  endpoint = ${quote(endpoint)}`,
    '',
    '  # api_key is read from the SOROUSH_API_KEY environment variable. Do not write it here: a saved',
    '  # plan file records literal provider values, so a key in this block ends up in that file.',
    '}',
    '',
  ].join('\n');
}
